import { CommonModule } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';

export type RadioValue = string | number | boolean;

export interface RadioOption {
	label: string;
	value: RadioValue;
	disabled?: boolean;
}

@Component({
	selector: 'app-radiobutton-reusable',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule],
	templateUrl: './radiobutton-reusable.html',
})
export class RadiobuttonReusable implements OnInit, OnChanges, OnDestroy {
	@Input() label = '';
	@Input() options: RadioOption[] = [];
	@Input() control: FormControl<RadioValue | null> | null = null;
	@Input() formGroup: FormGroup | null = null;
	@Input() formControlName = '';
	@Input() name = '';
	@Input() required = false;
	@Input() disabled = false;
	@Input() allowDeselect = false;
	@Input() direction: 'row' | 'column' = 'column';
	@Input() defaultValue: RadioValue | null = null;
	@Input() errorMessage = '';

	@Output() valueChange = new EventEmitter<RadioValue | null>();

	readonly generatedId = `radio-reusable-${Math.random().toString(36).slice(2, 11)}`;
	private readonly fallbackControl = new FormControl<RadioValue | null>(null);
	private baseValidator: ValidatorFn | null = null;
	private valueChangeSubscription?: Subscription;
	private disabledByInput = false;

	ngOnInit(): void {
		this.captureBaseValidator();
		this.applyRequiredValidation();
		this.applyDefaultValue();
		this.syncDisabledState();
		this.setupValueChangeEmission();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['control'] || changes['formControlName'] || changes['formGroup']) {
			this.captureBaseValidator();
			this.applyRequiredValidation();
			this.applyDefaultValue();
			this.syncDisabledState();
			this.setupValueChangeEmission();
		}

		if (changes['required']) {
			this.applyRequiredValidation();
		}

		if (changes['defaultValue'] || changes['options']) {
			this.applyDefaultValue();
		}

		if (changes['disabled']) {
			this.syncDisabledState();
		}
	}

	ngOnDestroy(): void {
		this.valueChangeSubscription?.unsubscribe();
	}

	get resolvedControl(): FormControl<RadioValue | null> {
		if (this.control) {
			return this.control;
		}

		if (this.formGroup && this.formControlName) {
			const abstractControl = this.formGroup.get(this.formControlName);
			if (abstractControl instanceof FormControl) {
				return abstractControl as FormControl<RadioValue | null>;
			}
		}

		return this.fallbackControl;
	}

	get hasBoundControl(): boolean {
		if (this.control) {
			return true;
		}

		return !!(this.formGroup && this.formControlName && this.formGroup.get(this.formControlName));
	}

	get groupName(): string {
		return this.name || this.formControlName || this.generatedId;
	}

	get shouldShowError(): boolean {
		const control = this.resolvedControl;
		return control.invalid && (control.touched || control.dirty);
	}

	get firstErrorMessage(): string {
		if (this.errorMessage.trim().length > 0) {
			return this.errorMessage;
		}

		const errors = this.resolvedControl.errors;
		if (!errors) {
			return '';
		}

		if (errors['required']) {
			return `${this.label || 'This field'} is required.`;
		}

		return 'Invalid selection.';
	}

	isOptionSelected(option: RadioOption): boolean {
		return this.resolvedControl.value === option.value;
	}

	isOptionDisabled(option: RadioOption): boolean {
		return this.disabled || this.resolvedControl.disabled || !!option.disabled;
	}

	onSelectionChange(option: RadioOption): void {
		if (this.isOptionDisabled(option)) {
			return;
		}

		this.resolvedControl.markAsTouched();

		if (!this.hasBoundControl) {
			this.resolvedControl.setValue(option.value);
			this.resolvedControl.markAsDirty();
			this.resolvedControl.updateValueAndValidity();
		}
	}

	onOptionMouseDown(event: MouseEvent, option: RadioOption): void {
		if (this.isOptionDisabled(option)) {
			return;
		}

		if (!this.allowDeselect || !this.isOptionSelected(option)) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.resolvedControl.setValue(null);
		this.resolvedControl.markAsDirty();
		this.resolvedControl.markAsTouched();
		this.resolvedControl.updateValueAndValidity();
	}

	markAsTouched(): void {
		this.resolvedControl.markAsTouched();
	}

	trackByValue(index: number, option: RadioOption): string {
		return `${index}-${String(option.value)}`;
	}

	private captureBaseValidator(): void {
		if (!this.hasBoundControl) {
			return;
		}

		this.baseValidator = this.resolvedControl.validator;
	}

	private applyRequiredValidation(): void {
		if (!this.hasBoundControl) {
			return;
		}

		const validators: ValidatorFn[] = [];
		if (this.baseValidator) {
			validators.push(this.baseValidator);
		}

		if (this.required) {
			validators.push(Validators.required);
		}

		this.resolvedControl.setValidators(validators);
		this.resolvedControl.updateValueAndValidity({ emitEvent: false });
	}

	private applyDefaultValue(): void {
		if (!this.hasBoundControl || this.defaultValue === null || this.defaultValue === undefined) {
			return;
		}

		const optionExists = this.options.some((option) => option.value === this.defaultValue);
		const controlValue = this.resolvedControl.value;

		if (optionExists && (controlValue === null || controlValue === undefined || controlValue === '')) {
			this.resolvedControl.setValue(this.defaultValue, { emitEvent: false });
			this.resolvedControl.updateValueAndValidity({ emitEvent: false });
		}
	}

	private syncDisabledState(): void {
		if (!this.hasBoundControl) {
			return;
		}

		if (this.disabled && this.resolvedControl.enabled) {
			this.resolvedControl.disable({ emitEvent: false });
			this.disabledByInput = true;
		}

		if (!this.disabled && this.disabledByInput && this.resolvedControl.disabled) {
			this.resolvedControl.enable({ emitEvent: false });
			this.disabledByInput = false;
		}
	}

	private setupValueChangeEmission(): void {
		this.valueChangeSubscription?.unsubscribe();

		if (!this.hasBoundControl) {
			return;
		}

		this.valueChangeSubscription = this.resolvedControl.valueChanges.subscribe((value) => {
			this.valueChange.emit(value as RadioValue | null);
		});
	}
}
